import { type FormEvent, useEffect, useState } from "react"
import styled from "styled-components"

import { EmptyState, ErrorNotice, PanelEyebrow, PrimaryButton } from "@features/portal/PortalPrimitives"
import api from "@utils/api.client"
import PortalShell from "../Common/PortalShell"

type AccessRole = "LOCATAIRE" | "PROPRIETAIRE"

interface PortalUser {
  id?: string
  _id?: string
  firstName: string
  lastName: string
  email: string
  role: string
  phone?: string
  propertyIds?: string[]
  unitAddress?: string
  createdAt?: string
  isVerified?: boolean
  invitationSent?: boolean
  inviteUrl?: string
}

interface PropertyOption {
  id?: string
  _id?: string
  address: string
  city?: string
}

const AccessGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(340px, 0.7fr) minmax(0, 1.3fr);
  gap: 1rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`

const Card = styled.section`
  min-width: 0;
  padding: clamp(1.35rem, 3vw, 2.2rem);
  border: 1px solid #c4d0da;
  border-radius: 2px;
  background: #ffffff;
  box-shadow: 0 18px 50px rgba(8, 36, 60, 0.05);

  h2 {
    margin: 0;
    color: #102d49;
    font-size: clamp(1.45rem, 3vw, 2rem);
    letter-spacing: -0.035em;
  }

  > p {
    margin: 0.7rem 0 1.3rem;
    color: #677c8e;
    font-size: 0.88rem;
    line-height: 1.6;
  }
`

const Form = styled.form`
  display: grid;
  gap: 0.9rem;
`

const Field = styled.label`
  display: grid;
  gap: 0.4rem;
  color: #183953;
  font-size: 0.82rem;
  font-weight: 750;

  input,
  select {
    width: 100%;
    min-height: 49px;
    padding: 0 0.85rem;
    border: 1px solid #b9c7d2;
    border-radius: 2px;
    background: #ffffff;
    color: #102d49;
    font: inherit;
  }

  input:focus,
  select:focus {
    outline: 3px solid rgba(51, 122, 189, 0.12);
    border-color: #337abd;
  }
`

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`

const PropertyChoices = styled.fieldset`
  display: grid;
  gap: 0.45rem;
  margin: 0;
  padding: 0.85rem;
  border: 1px solid #c7d2dc;

  legend {
    padding: 0 0.35rem;
    color: #183953;
    font-size: 0.8rem;
    font-weight: 750;
  }

  label {
    display: flex;
    align-items: flex-start;
    gap: 0.55rem;
    color: #3d586d;
    font-size: 0.82rem;
    line-height: 1.4;
  }

  input {
    margin-top: 0.17rem;
  }
`

const Hint = styled.p`
  margin: -0.2rem 0 0;
  color: #728495;
  font-size: 0.74rem;
  line-height: 1.5;
`

const UserList = styled.div`
  display: grid;
  margin-top: 1rem;
  border-top: 1px solid #cfd9e1;
`

const UserRow = styled.article`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid #d6dfe6;

  strong {
    display: block;
    color: #153b5b;
  }

  p {
    margin: 0.28rem 0 0;
    color: #6b7f90;
    font-size: 0.82rem;
  }

  span {
    align-self: start;
    padding: 0.34rem 0.52rem;
    border: 1px solid #a9c5d9;
    background: #eef6fb;
    color: #245e89;
    font-size: 0.66rem;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  button {
    margin-top: 0.55rem;
    padding: 0;
    border: 0;
    background: transparent;
    color: #276b9f;
    font: inherit;
    font-size: 0.74rem;
    font-weight: 750;
    cursor: pointer;
  }
`

const Access = () => {
  const [users, setUsers] = useState<PortalUser[]>([])
  const [properties, setProperties] = useState<PropertyOption[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    role: "LOCATAIRE" as AccessRole,
    unitAddress: "",
    propertyIds: [] as string[],
  })

  useEffect(() => {
    Promise.all([api.get<PortalUser[]>("/users"), api.get<PropertyOption[]>("/properties-to-rent")])
      .then(([usersResponse, propertiesResponse]) => {
        setUsers(usersResponse.data)
        setProperties(propertiesResponse.data)
      })
      .catch(() => setError("Les accès existants ne peuvent pas tous être chargés."))
      .finally(() => setLoading(false))
  }, [])

  const setField = (key: keyof typeof form, value: string | string[]) => {
    setForm((current) => ({ ...current, [key]: value }))
  }

  const toggleProperty = (id: string) => {
    setForm((current) => ({
      ...current,
      propertyIds: current.propertyIds.includes(id) ? current.propertyIds.filter((propertyId) => propertyId !== id) : [...current.propertyIds, id],
    }))
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setSaving(true)
    setError("")
    setSuccess("")
    try {
      const response = await api.post<PortalUser>("/users/register", form)
      setUsers((current) => [response.data, ...current])
      setSuccess(
        response.data.invitationSent
          ? `L’invitation de ${response.data.firstName || response.data.email} a été envoyée par courriel.`
          : `L’accès est créé, mais le courriel n’a pas été envoyé. Vérifiez la configuration SMTP puis renvoyez l’invitation.`,
      )
      setForm((current) => ({
        ...current,
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        unitAddress: "",
        propertyIds: [],
      }))
    } catch (requestError: unknown) {
      const message = (requestError as { message?: string })?.message
      setError(message || "L’accès n’a pas pu être créé.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <PortalShell title="Accès utilisateurs" eyebrow="Administration">
      <AccessGrid>
        <Card>
          <PanelEyebrow>Nouvel accès</PanelEyebrow>
          <h2>Inviter une personne</h2>
          <p>Attribuez le bon rôle. La personne recevra un lien à usage unique pour choisir elle-même son mot de passe.</p>
          <Form onSubmit={submit}>
            <FieldRow>
              <Field>
                Prénom
                <input value={form.firstName} onChange={(event) => setField("firstName", event.target.value)} autoComplete="off" />
              </Field>
              <Field>
                Nom
                <input value={form.lastName} onChange={(event) => setField("lastName", event.target.value)} autoComplete="off" />
              </Field>
            </FieldRow>
            <Field>
              Adresse courriel
              <input type="email" value={form.email} onChange={(event) => setField("email", event.target.value)} autoComplete="off" required />
            </Field>
            <FieldRow>
              <Field>
                Rôle
                <select value={form.role} onChange={(event) => setField("role", event.target.value)}>
                  <option value="LOCATAIRE">Locataire</option>
                  <option value="PROPRIETAIRE">Propriétaire</option>
                </select>
              </Field>
              <Field>
                Téléphone
                <input type="tel" value={form.phone} onChange={(event) => setField("phone", event.target.value)} autoComplete="off" />
              </Field>
            </FieldRow>
            <Field>
              {form.role === "LOCATAIRE" ? "Adresse et unité du locataire" : "Adresse principale ou note"}
              <input
                value={form.unitAddress}
                onChange={(event) => setField("unitAddress", event.target.value)}
                placeholder={form.role === "LOCATAIRE" ? "123, rue Exemple — unité 4" : "Facultatif"}
                required={form.role === "LOCATAIRE"}
              />
            </Field>
            {properties.length > 0 && (
              <PropertyChoices>
                <legend>Immeubles visibles</legend>
                {properties.map((property) => {
                  const id = property.id || property._id
                  if (!id) return null
                  return (
                    <label key={id}>
                      <input type="checkbox" checked={form.propertyIds.includes(id)} onChange={() => toggleProperty(id)} />
                      {property.address}
                      {property.city ? `, ${property.city}` : ""}
                    </label>
                  )
                })}
              </PropertyChoices>
            )}
            <Hint>L’invitation expire après 72 heures. Aucun mot de passe ne circule par courriel.</Hint>
            {error && <ErrorNotice role="alert">{error}</ErrorNotice>}
            {success && <ErrorNotice style={{ borderColor: "#4c9867", background: "#eef8f1", color: "#2e6542" }}>{success}</ErrorNotice>}
            <PrimaryButton type="submit" disabled={saving}>
              {saving ? "Envoi…" : "Envoyer l’invitation"}
            </PrimaryButton>
          </Form>
        </Card>

        <Card>
          <PanelEyebrow>Répertoire</PanelEyebrow>
          <h2>Accès actifs</h2>
          <p>La liste ne montre jamais les mots de passe.</p>
          {loading ? (
            <EmptyState>Chargement des accès…</EmptyState>
          ) : users.length ? (
            <UserList>
              {users.map((user) => (
                <UserRow key={user.id || user._id || user.email}>
                  <div>
                    <strong>{`${user.firstName || ""} ${user.lastName || ""}`.trim() || user.email}</strong>
                    <p>
                      {user.email}
                      {user.unitAddress ? ` · ${user.unitAddress}` : ""}
                    </p>
                    {!user.isVerified && (
                      <button
                        type="button"
                        onClick={async () => {
                          const id = user.id || user._id
                          if (!id) return
                          setError("")
                          setSuccess("")
                          try {
                            const response = await api.post<{ emailSent: boolean }>(`/users/${id}/resend-invitation`, {})
                            setSuccess(
                              response.data.emailSent
                                ? `Une nouvelle invitation a été envoyée à ${user.email}.`
                                : "L’invitation a été renouvelée, mais le courriel n’a pas été envoyé.",
                            )
                          } catch {
                            setError("L’invitation n’a pas pu être renouvelée.")
                          }
                        }}
                      >
                        Renvoyer l’invitation
                      </button>
                    )}
                  </div>
                  <span>{user.role === "LOCATAIRE" ? "Locataire" : user.role === "ADMIN" ? "Admin" : "Propriétaire"}</span>
                </UserRow>
              ))}
            </UserList>
          ) : (
            <EmptyState>Aucun accès à afficher.</EmptyState>
          )}
        </Card>
      </AccessGrid>
    </PortalShell>
  )
}

export default Access
